import React, { useEffect, useState } from 'react';
import { IonContent, IonPage, IonRefresher, IonRefresherContent } from '@ionic/react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppHeader, StoreFilterBar, PeriodTabs, RankedTable } from '../components';
import type { GroupBy, Period, RegionRow } from '../api/types';
import { selectPeriod, setCities, setRegions } from '../state/filtersSlice';
import { formatInt } from '../utils/format';
import { regionColumns } from './states/regionColumns';
import { storesLink } from './stores/parseStoresQuery';
import { useLastRefreshed } from '../hooks/useLastRefreshed';
import { useRegionBreakdown } from './stores/useSuFetch';
import './stores/stores.css';
import './states/states.css';

export const STATE_CITIES_PATH = '/app/state-cities';

/**
 * `drill` = the city list of one state (route /app/state-cities?state=X). It is its own route so the browser/Ionic
 * back button returns to the state list, and so the State Wise tab (which remembers its last URL) keeps opening
 * the state list.
 */
const StateWisePage: React.FC<{ drill?: boolean }> = ({ drill = false }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const period = useSelector((s: any) => selectPeriod(s)) as Period;
  // Drill-down: all states -> one state's cities -> that city's stores (Stores screen).
  const urlState = location.pathname === STATE_CITIES_PATH ? new URLSearchParams(location.search).get('state') : null;
  // Sticky: while this page sits in the stack behind another screen the URL changes, but the content must not.
  const [stateName, setStateName] = useState<string | null>(urlState);
  useEffect(() => {
    if (urlState) setStateName(urlState);
  }, [urlState]);
  const selectedState = drill ? stateName : null;
  const groupBy: GroupBy = selectedState ? 'city' : 'state';
  const label = groupBy === 'state' ? 'State' : 'City';

  // The WTD and MTD tabs are one "WTD + MTD" tab: 'mtd' returns WTD as its compare period.
  const tablePeriod: Period = period === 'wtd' ? 'mtd' : period;
  const table = useRegionBreakdown(groupBy, tablePeriod, true, selectedState);

  const comparePeriod = table.data?.compare_period ?? null;
  const shownPeriod = table.data?.period ?? tablePeriod;
  const { columns, groups } = regionColumns(groupBy, shownPeriod, comparePeriod);

  const open = (r: RegionRow) => {
    if (groupBy === 'state') {
      history.push(`${STATE_CITIES_PATH}?state=${encodeURIComponent(r.key)}`);
    } else {
      dispatch(setRegions([r.state]));
      dispatch(setCities([r.key]));
      history.push(storesLink({ city: r.key, region: r.state }));
    }
  };

  const back = () => (history.action !== 'POP' ? history.goBack() : history.replace('/app/states'));

  const onRefresh = (e: CustomEvent) => {
    table.refetch();
    setTimeout(() => (e.target as any).complete(), 600);
  };

  const lastRefreshed = useLastRefreshed();
  const total = table.data?.total_stores;
  const failed = table.error && !table.data;

  return (
    <IonPage>
      <AppHeader
        title={drill && selectedState ? selectedState : undefined}
        subtitle={lastRefreshed}
        onBack={drill ? back : undefined}
        onSearch={() => history.push('/app/stores?focus=search')}
      />
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={onRefresh}>
          <IonRefresherContent />
        </IonRefresher>
        <div className="su-page" style={{ paddingTop: 0 }}>
          <StoreFilterBar count={total} />
          <PeriodTabs />
          {selectedState && (
            <button type="button" className="su-link su-back-link" onClick={back}>
              &lt; All States
            </button>
          )}

          {failed ? (
            <div className="su-card su-error">
              <div>Could not load {label.toLowerCase()} data.</div>
              <button type="button" className="su-btn" onClick={() => table.refetch()}>Retry</button>
            </div>
          ) : (
            <>
              <div className={`su-states-table${comparePeriod ? ' dual' : ''}`}>
                <RankedTable
                  title={
                    <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span>{selectedState ? `${selectedState} – ` : ''}{label} Wise Performance</span>
                      {total != null && <span className="su-muted" style={{ fontWeight: 400, fontSize: 12 }}>Total Stores {formatInt(total)}</span>}
                    </span>
                  }
                  columns={columns}
                  headerGroups={groups}
                  rows={table.data?.items ?? null}
                  rowKey={(r) => `${r.state}:${r.key}`}
                  loading={table.loading}
                  skeletonRows={8}
                  emptyText="No data"
                  onRowClick={open}
                  onViewAll={selectedState ? () => {
                    dispatch(setRegions([selectedState]));
                    dispatch(setCities([]));
                    history.push(storesLink({ region: selectedState }));
                  } : undefined}
                  viewAllLabel="All Stores >"
                />
              </div>
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default StateWisePage;
